import clsx from "clsx";
import { FC, Ref, useEffect, useRef } from "react";

import { CourseType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import {
  ClickEvent,
  Menu,
  MenuDirection,
  MenuInstance,
  MenuItem,
  MenuType,
  SubMenu,
} from "mds/components/Menu";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ORDER_BY_CODE } from "mds/hooks/use-ordered";
import { ChevronDownIcon, TextBooksIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { courseSelectors, selectAdminAccessibleCourses } from "store/selectors";
import { getCourseDisplayTitle } from "utils/course";

export type CourseMenuProps = {
  menuClassName?: string;
  selectedCourseId: string | null;
  onCourseChange: (courseId: string) => void;
  menuType?: MenuType;
  defaultToAll?: boolean;
  adminAccessibleOnly?: boolean;
  showEmptyOption?: boolean;
  size?: "xs" | "s";
  direction?: MenuDirection;
};

// This is very similar to TermMenu, consider refactoring to a shared general dropdown component
export const CourseMenu: FC<CourseMenuProps> = ({
  menuClassName,
  selectedCourseId,
  onCourseChange,
  menuType = "submenu",
  defaultToAll = true,
  showEmptyOption = true,
  adminAccessibleOnly = false,
  size = "xs",
  direction = "bottom",
}) => {
  const subMenuRef: Ref<MenuInstance> = useRef(null);
  const courses = useAppSelector(
    adminAccessibleOnly ? selectAdminAccessibleCourses : courseSelectors.selectAll,
  ).toSorted(ORDER_BY_CODE);
  const emptyCourseOption: CourseType = {
    id: "",
    code: defaultToAll ? t("import_modal.all_courses") : t("import_modal.select_course"),
    title: "",
    grouping_id: null,
  };
  const courseOptions = showEmptyOption ? [emptyCourseOption, ...courses] : courses;
  const selectedCourse = courseOptions.find((course) => course?.id === selectedCourseId);

  useEffect(() => {
    storeApi.courses.list();
  }, []);

  const onItemClick = (e: ClickEvent) => {
    const newCourse = e.value as CourseType;
    onCourseChange(newCourse.id);
    // This is a supported feature of szhsin/react-menu which allows
    // the menu to stay open even if a menu item is clicked. We use this to keep parent menus open
    // eslint-disable-next-line no-param-reassign
    e.keepOpen = true;
    subMenuRef.current?.closeMenu();
  };

  const menuButton = (
    <SquareButton
      className="w-full"
      isSelected={menuType === "menu" ? undefined : false}
      kind="tertiary"
      size={size}
    >
      <IconText
        iconStart={<TextBooksIcon />}
        text={selectedCourse && getCourseDisplayTitle(selectedCourse)}
      />
      <ChevronDownIcon />
    </SquareButton>
  );

  const DropdownMenu = courseOptions.map((courseOption) => {
    const selected = selectedCourseId === courseOption.id;
    return (
      <MenuItem
        checked={selected}
        className={clsx({ selected, "text-white": selected }, "center dropdown-item")}
        key={courseOption.id}
        value={courseOption}
        onClick={onItemClick}
      >
        <IconText iconStart={<TextBooksIcon />} text={getCourseDisplayTitle(courseOption)} />
      </MenuItem>
    );
  });

  const menuProps = {
    className: "dropdown",
    instanceRef: subMenuRef,
    children: DropdownMenu,
    size,
    direction,
  };

  return menuType === "menu" ? (
    <Menu
      {...menuProps}
      direction={direction}
      menuButton={menuButton}
      menuClassName={menuClassName}
      position="initial"
    />
  ) : (
    <SubMenu
      {...menuProps}
      direction={direction}
      label={menuButton}
      openTrigger="clickOnly"
      position="initial"
    />
  );
};
